import { useEffect, useState } from "react";
import {
  Users, Calendar, HandHelping, Brain, Footprints, Clock, DollarSign,
  Heart, MapPin, AlertCircle,
} from "lucide-react";
import { api } from "@/lib/api";

// Icon per known quiz question id. Falls back to Heart if unknown.
const ICONS = {
  searching_for: Users,
  age_range: Calendar,
  independence_level: HandHelping,
  memory_concerns: Brain,
  mobility: Footprints,
  daily_help: HandHelping,
  timeline: Clock,
  budget: DollarSign,
};

// Soft "highlight when answer is concerning" — used to surface urgency in the
// summary chip row. Lets partners scan a lead in 2 seconds.
const URGENCY_VALUES = new Set([
  "urgent", "limited", "wheelchair", "significant", "yes", "85_plus", "often", "daily",
]);

function labelize(text) {
  return text.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function QuizAnswers({ answers = {}, careType = null }) {
  const [questions, setQuestions] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/quiz/public")
      .then(({ data }) => { if (!cancelled) setQuestions(data.questions || []); })
      .catch(() => { if (!cancelled) setQuestions([]); });
    return () => { cancelled = true; };
  }, []);

  const entries = Object.entries(answers || {});
  if (entries.length === 0) {
    return <div className="text-sm text-[#5C6B62] italic">No quiz answers on file.</div>;
  }

  // Build map: id -> {label, optionsByValue, type}
  const qById = {};
  (questions || []).forEach((q) => {
    qById[q.id] = {
      label: q.label,
      type: q.type,
      optionsByValue: Object.fromEntries((q.options || []).map((o) => [o.value, o.label])),
    };
  });

  // Quick-glance summary chips at the top — give partners a 2-second skim.
  const summaryChips = entries
    .map(([id, value]) => {
      const meta = qById[id];
      const valueStr = Array.isArray(value) ? value.join(", ") : String(value);
      const display = meta?.optionsByValue[valueStr] || labelize(valueStr);
      const urgent = URGENCY_VALUES.has(valueStr);
      return { id, display, urgent };
    })
    .filter((c) => c.display);

  return (
    <div className="space-y-5">
      {/* Care type spotlight */}
      {careType && (
        <div className="rounded-2xl bg-gradient-to-br from-[#2C4C3B] to-[#1A2E24] text-white p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
            <Heart className="w-6 h-6 text-[#9CAF88]" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#9CAF88] font-semibold mb-1">
              Recommended care
            </div>
            <div className="font-display text-2xl font-semibold">{careType}</div>
          </div>
        </div>
      )}

      {/* At-a-glance chips */}
      {summaryChips.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="quiz-answers-summary">
          {summaryChips.map((c) => (
            <span
              key={c.id}
              className={
                "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium " +
                (c.urgent
                  ? "bg-[#E07A5F]/15 text-[#C96B52] border border-[#E07A5F]/30"
                  : "bg-[#9CAF88]/15 text-[#2C4C3B] border border-[#9CAF88]/30")
              }
            >
              {c.urgent && <AlertCircle className="w-3 h-3" />}
              {c.display}
            </span>
          ))}
        </div>
      )}

      {/* Per-question detail cards */}
      <div className="grid sm:grid-cols-2 gap-3">
        {entries.map(([id, value]) => {
          const meta = qById[id];
          const Icon = ICONS[id] || Heart;
          const valueStr = Array.isArray(value) ? value.join(", ") : String(value);
          const valueDisplay = meta?.optionsByValue[valueStr] || labelize(valueStr);
          const questionLabel = meta?.label || labelize(id);
          return (
            <div
              key={id}
              className="rounded-2xl bg-white border border-[#E2DFD8] p-4 flex gap-3"
              data-testid={`quiz-answer-${id}`}
            >
              <div className="w-9 h-9 rounded-lg bg-[#9CAF88]/15 flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-[#2C4C3B]" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-wider text-[#9CAF88] font-semibold mb-0.5">
                  {questionLabel}
                </div>
                <div className="font-medium text-[#1A2E24] text-sm leading-snug">
                  {valueDisplay}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { MapPin };  // keep import surface stable for future re-use
